import styles from "./DragPreview.module.css"

import { startDragPreview as startPreview } from "@shared/components/drag-preview/startDragPreview"
import { cx } from "@shared/components/untitledui/util/cx"

/**
 * Class on <body> while a node drag is in flight. unified-tiptap.css uses it to hide the
 * browser's native drag caret in the editors — the drop indicator line already shows
 * where the block will land.
 */
const DRAGGING_CLASS = "unified-tiptap-dragging"

/**
 * The editor's drag preview: the dragged node on a white card that springs after the
 * pointer. Call from a dragstart handler. Only the ghost is replaced — ProseMirror's drag
 * logic (view.dragging, drop handling, drop cursor) runs as usual.
 */
export function startDragPreview(event: DragEvent, sourceEl: HTMLElement) {
  // Nodes whose visible content doesn't fill their wrapper (e.g. images sized fit-content)
  // mark the visual box with data-drag-preview-size; size the preview to it instead of the
  // wrapper so the card hugs the content. Ignore markers inside nested node views (dragging
  // a card shouldn't shrink to an image inside it).
  const marked = sourceEl.querySelector<HTMLElement>("[data-drag-preview-size]")
  const sizeEl = marked && marked.closest("[data-node-view-wrapper]") === sourceEl ? marked : null

  startPreview(event, sourceEl, {
    // A titled card travels as its title alone: mid-drag the preview is a handle, and a full
    // card in miniature hides the planner underneath it.
    className: cx(styles.card, sourceEl.querySelector(".node-cardTitle") && styles.titleOnly),
    bodyClass: DRAGGING_CLASS,
    contentWidth: sizeEl?.getBoundingClientRect().width,
    // Recreate the editor's class scope around the clone, since the preview lives outside
    // it: the scope classes provide the editor's descendant rules (heading sizes, list
    // styles — unified-tiptap.css), while the copied computed styles provide the values the
    // editor inherits from its own ancestors (app font-family, text color). Both are
    // needed; neither subsumes the other.
    wrap: (clone, preview) => {
      const editorRoot = sourceEl.closest(".unified-tiptap-component")
      const scope = document.createElement("div")
      if (editorRoot) {
        scope.className = editorRoot.className
        const editorStyle = getComputedStyle(editorRoot)
        preview.style.fontFamily = editorStyle.fontFamily
        preview.style.color = editorStyle.color
      }
      scope.appendChild(clone)
      return scope
    },
  })
}
